import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { DocumentDate } from '@features/invoices/elements/DocumentDate'
import { DocumentDetailsCard } from '@features/invoices/elements/DocumentDetailsCard'

import { useQuote } from '../../../../contexts/quoteContext'
import { useDisplayQuoteDetailsCard } from '../../../../hooks/useDisplayQuoteDetailsCard'
import { QuoteRelatedInvoices } from './elements/QuoteRelatedInvoices'
import { QuoteStatus } from './elements/QuoteStatus'

export const QuoteDetailsCard = (): ReactElement | null => {
  const { t } = useTranslation()
  const { quote } = useQuote()

  const {
    isLoading,
    hasRelatedInvoices,
    shouldDisplayStatus,
    shouldDisplayCustomerLink,
    shouldDisplayDueDate,
    shouldDisplayIssueDate,
    shouldDisplayRelatedDocumentLinks,
    shouldDisplayQuoteDetailsCard,
  } = useDisplayQuoteDetailsCard()

  if (!quote || !shouldDisplayQuoteDetailsCard) {
    return null
  }

  const issueDateTitle = hasRelatedInvoices
    ? t('quotes.view.sidebar.details_card.quote_created')
    : t('quotes.view.sidebar.details_card.issue_date')

  const customerLinkEntry = shouldDisplayCustomerLink && (
    <DocumentDetailsCard.Entry title={t('quotes.view.sidebar.details_card.customer')}>
      <DocumentDetailsCard.CustomerLink
        displayName={quote.customer?.displayName}
        externalContactId={quote.customer?.externalContactId}
      />
    </DocumentDetailsCard.Entry>
  )

  const issueDateEntry = shouldDisplayIssueDate && (
    <DocumentDetailsCard.Entry title={issueDateTitle}>
      <DocumentDate date={quote.issueDate} />
    </DocumentDetailsCard.Entry>
  )

  return (
    <>
      <DocumentDetailsCard isLoading={isLoading}>
        {shouldDisplayStatus && (
          <DocumentDetailsCard.Entry title={t('quotes.view.sidebar.details_card.status')}>
            <QuoteStatus />
          </DocumentDetailsCard.Entry>
        )}
        {shouldDisplayRelatedDocumentLinks && (
          <DocumentDetailsCard.Entry title={t('quotes.view.sidebar.details_card.related_to')}>
            <QuoteRelatedInvoices />
          </DocumentDetailsCard.Entry>
        )}
        {!hasRelatedInvoices && customerLinkEntry}
        {!hasRelatedInvoices && issueDateEntry}
        {shouldDisplayDueDate && (
          <DocumentDetailsCard.Entry title={t('quotes.view.sidebar.details_card.due_date')}>
            <DocumentDate date={quote.dueDate} />
          </DocumentDetailsCard.Entry>
        )}
      </DocumentDetailsCard>
      {hasRelatedInvoices && (shouldDisplayCustomerLink || shouldDisplayIssueDate) && (
        <DocumentDetailsCard isLoading={isLoading}>
          {customerLinkEntry}
          {issueDateEntry}
        </DocumentDetailsCard>
      )}
    </>
  )
}
